import { ChatApiError } from "./api.ts";

/**
 * Le quiz du lecteur sur le livre en cours (serveur IA : modules/quiz), repris de Studio. Le serveur écrit les
 * questions, connaît les réponses et compte les points ; la réponse d'une question n'arrive qu'une fois résolue.
 */

export type QuizScope = "current" | "previous";

export interface QuizRules {
  first_try: number;
  second_try: number;
  bonus: number;
  max_points: number;
}

export interface QuizOptions {
  book_id: string;
  scopes: QuizScope[];
  /** Quiz commencés sur ces portées, à reprendre (même une fois les quiz du jour faits). */
  begun?: Partial<Record<QuizScope, string>>;
  section_label: string;
  /** null : sans limite (administrateurs). */
  left_today: number | null;
  rules: QuizRules;
}

export interface QuizScore {
  completed: number;
  points: number;
  max_points: number;
  percent: number | null;
}

export interface QuizQuestion {
  index: number;
  question: string;
  choices: string[];
  tries: number[];
  tries_left: number;
  resolved: boolean;
  points: number;
  // Une fois résolue :
  answer?: number;
  correct?: boolean;
  first_try?: boolean;
  explanation?: string;
  section?: string | null;
}

export interface Quiz {
  id: string;
  book_id: string;
  scope: QuizScope;
  section_label: string;
  /** La langue dans laquelle il est écrit (celle du livre, ou de l'interface choisie à son lancement). */
  language?: string | null;
  status: "in_progress" | "completed";
  points: number;
  bonus: number;
  total: number | null;
  max_points: number;
  created_at: string;
  completed_at: string | null;
  questions?: QuizQuestion[];
}

export interface QuizAnswer {
  correct: boolean;
  tries_left: number;
  points: number;
  counted: boolean;
  question: QuizQuestion;
  quiz: Quiz;
  book_score?: QuizScore;
}

export interface QuizList {
  quizzes: Quiz[];
  score: QuizScore;
  rules: QuizRules;
}

/** Où est le lecteur : le fichier lu, les fichiers du livre dans l'ordre, et le titre de la section. */
export interface QuizPosition {
  href: string;
  readingOrder: string[];
  section?: string;
}

/** Pourquoi un appel a échoué, tel qu'on le dit au lecteur. */
export type QuizFailure = "unavailable" | "daily_limit" | "too_short" | "not_found" | "other";

export function quizFailure(error: unknown): QuizFailure {
  if (!(error instanceof ChatApiError)) return "other";
  if (error.status === 404) return "unavailable";
  if (error.status === 429) return "daily_limit";
  if (error.status === 422 && /court|short/i.test(error.message)) return "too_short";
  if (error.status === 422) return "not_found";
  return "other";
}

/** La première question à montrer : la première non résolue, ou la première d'un quiz fini (relu). */
export function firstQuestion(quiz: Quiz): number {
  if (quiz.status === "completed") return 0;
  return Math.max(0, quiz.questions?.findIndex((question) => !question.resolved) ?? 0);
}

/** Le quiz après une réponse : la question mise à jour, l'état du quiz (points, statut) repris du serveur. */
export function withAnswer(quiz: Quiz, outcome: QuizAnswer): Quiz {
  const questions = (quiz.questions ?? []).map((item) => (item.index === outcome.question.index ? outcome.question : item));
  return { ...quiz, ...outcome.quiz, questions };
}
